import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  AlertCircle, 
  Loader2,
  Star,
  Download,
  Eye,
  Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  uploadProductImage, 
  validateImageFile, 
  formatFileSize 
} from '../services/imageService';
import { toast } from 'sonner';

interface StagedImage {
  id: string;
  file?: File;
  url: string;
  isUploaded: boolean;
  isUploading?: boolean;
  isPrimary?: boolean;
  error?: string;
}

interface ImageUploadProps {
  existingImages?: string[];
  onImagesChange: (images: string[]) => void;
  onPrimaryImageChange: (primaryImage: string) => void;
  primaryImage?: string;
  maxImages?: number;
  className?: string;
  onImageMarkedForDeletion?: (imageUrl: string) => void;
}

export function ImageUpload({ 
  existingImages = [], 
  onImagesChange, 
  onPrimaryImageChange,
  primaryImage,
  maxImages = 10,
  className = "",
  onImageMarkedForDeletion
}: ImageUploadProps) {
  const [stagedImages, setStagedImages] = useState<StagedImage[]>(() => {
    return existingImages.map((url, index) => ({
      id: `existing-${index}`,
      url,
      isUploaded: true,
      isPrimary: url === primaryImage
    }));
  });
  
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync with external changes
  React.useEffect(() => {
    const existingSet = new Set(existingImages);
    const currentUrls = stagedImages.map(img => img.url);
    const needsUpdate = existingImages.length !== stagedImages.filter(img => img.isUploaded).length ||
                       !existingImages.every(url => currentUrls.includes(url));
    
    if (needsUpdate) {
      setStagedImages(existingImages.map((url, index) => ({
        id: `existing-${index}`,
        url,
        isUploaded: true,
        isPrimary: url === primaryImage
      })));
    }
  }, [existingImages, primaryImage]);

  // Sync primary image changes
  React.useEffect(() => {
    setStagedImages(prev => prev.map(img => ({
      ...img,
      isPrimary: img.url === primaryImage
    })));
  }, [primaryImage]);

  const handleFileSelect = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    // Check if adding these files would exceed the limit
    const currentImageCount = stagedImages.length;
    const newImageCount = fileArray.length;
    
    if (currentImageCount + newImageCount > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed. You can add ${maxImages - currentImageCount} more.`);
      return;
    }

    // Validate files
    const validFiles: File[] = [];
    const invalidFiles: { file: File; error: string }[] = [];

    fileArray.forEach(file => {
      const error = validateImageFile(file);
      if (error) {
        invalidFiles.push({ file, error });
      } else {
        validFiles.push(file);
      }
    });

    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ file, error }) => {
        toast.error(`${file.name}: ${error}`);
      });
    }

    if (validFiles.length === 0) return;

    // Add files to staging area
    const newStagedImages: StagedImage[] = validFiles.map(file => ({
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      url: URL.createObjectURL(file),
      isUploaded: false,
      isUploading: false
    }));

    setStagedImages(prev => [...prev, ...newStagedImages]);
    toast.success(`${validFiles.length} image(s) staged for upload`);
  }, [stagedImages.length, maxImages]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
    // Reset the input so the same file can be selected again
    e.target.value = '';
  }, [handleFileSelect]);

  const removeImage = useCallback((imageId: string) => {
    setStagedImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      const newImages = prev.filter(img => img.id !== imageId);
      
      // If removing a Firebase Storage image, mark it for deletion
      if (imageToRemove?.isUploaded && imageToRemove.url.includes('firebasestorage.googleapis.com') && onImageMarkedForDeletion) {
        onImageMarkedForDeletion(imageToRemove.url);
      }
      
      // Update the images in parent
      const uploadedUrls = newImages.filter(img => img.isUploaded).map(img => img.url);
      onImagesChange(uploadedUrls);
      
      // Update primary image if needed
      const removedImage = prev.find(img => img.id === imageId);
      if (removedImage?.isPrimary && newImages.length > 0) {
        const newPrimary = newImages.find(img => img.isUploaded);
        if (newPrimary) {
          onPrimaryImageChange(newPrimary.url);
        } else {
          onPrimaryImageChange('');
        }
      }
      
      return newImages;
    });
    
    toast.success('Image removed');
  }, [onImagesChange, onPrimaryImageChange, onImageMarkedForDeletion]);

  const setPrimary = useCallback((imageId: string) => {
    setStagedImages(prev => {
      const updated = prev.map(img => ({
        ...img,
        isPrimary: img.id === imageId
      }));
      
      const primaryImg = updated.find(img => img.id === imageId);
      if (primaryImg?.isUploaded) {
        onPrimaryImageChange(primaryImg.url);
      }
      
      return updated;
    });
  }, [onPrimaryImageChange]);

  const uploadStagedImages = useCallback(async () => {
    const unuploadedImages = stagedImages.filter(img => !img.isUploaded && img.file);
    
    if (unuploadedImages.length === 0) {
      return;
    }

    setIsUploading(true);
    
    try {
      // Mark images as uploading
      setStagedImages(prev => prev.map(img => 
        !img.isUploaded ? { ...img, isUploading: true } : img
      ));

      const uploadPromises = unuploadedImages.map(async (stagedImg) => {
        try {
          const url = await uploadProductImage(stagedImg.file!);
          return { id: stagedImg.id, url, success: true };
        } catch (error) {
          console.error('Upload failed for image:', error);
          return { 
            id: stagedImg.id, 
            url: '', 
            success: false, 
            error: error instanceof Error ? error.message : 'Upload failed' 
          };
        }
      });

      const results = await Promise.all(uploadPromises);
      
      // Update staged images with results
      setStagedImages(prev => {
        const updated = prev.map(img => {
          const result = results.find(r => r.id === img.id);
          if (result) {
            if (result.success) {
              // Revoke object URL to free memory
              if (img.url.startsWith('blob:')) {
                URL.revokeObjectURL(img.url);
              }
              return {
                ...img,
                url: result.url,
                isUploaded: true,
                isUploading: false,
                error: undefined
              };
            } else {
              return {
                ...img,
                isUploading: false,
                error: result.error
              };
            }
          }
          return img;
        });
        
        // Update parent with new uploaded URLs
        const uploadedUrls = updated.filter(img => img.isUploaded).map(img => img.url);
        onImagesChange(uploadedUrls);
        
        // Set primary image if none is set
        if (!primaryImage && updated.length > 0) {
          const firstUploaded = updated.find(img => img.isUploaded);
          if (firstUploaded) {
            onPrimaryImageChange(firstUploaded.url);
            return updated.map(img => ({
              ...img,
              isPrimary: img.id === firstUploaded.id
            }));
          }
        }
        
        return updated;
      });

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      if (successCount > 0) {
        toast.success(`${successCount} image(s) uploaded successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} image(s) failed to upload`);
      }
      
    } catch (error) {
      console.error('Upload process failed:', error);
      toast.error('Failed to upload images');
    } finally {
      setIsUploading(false);
    }
  }, [stagedImages, onImagesChange, onPrimaryImageChange, primaryImage]);

  const unuploadedCount = stagedImages.filter(img => !img.isUploaded).length;
  const canUpload = unuploadedCount > 0 && !isUploading;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <Card className="relative overflow-hidden">
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${
            isDragOver 
              ? 'border-jamaica-green bg-jamaica-green/5 scale-[1.02]' 
              : 'border-gray-300 hover:border-jamaica-green/50 hover:bg-gray-50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />
          
          <div className="space-y-4">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center transition-all duration-300 ${
              isDragOver ? 'bg-jamaica-green text-white' : 'bg-gray-100 text-gray-400'
            }`}>
              <Upload className="w-8 h-8" />
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {isDragOver ? 'Drop images here' : 'Upload Product Images'}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Drag and drop images here, or click to browse
                <br />
                <span className="text-xs">
                  Supports: JPEG, PNG, WEBP, GIF • Max size: 5MB • Max {maxImages} images
                </span>
              </p>
              
              <div className="flex justify-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={stagedImages.length >= maxImages}
                  className="hover:bg-jamaica-green hover:text-white transition-all duration-300"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Browse Files
                </Button>
                
                {canUpload && (
                  <Button
                    type="button"
                    onClick={uploadStagedImages}
                    disabled={isUploading}
                    className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
                  >
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload {unuploadedCount} Image{unuploadedCount !== 1 ? 's' : ''}
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {stagedImages.length >= maxImages && (
            <div className="absolute inset-0 bg-gray-100/80 flex items-center justify-center rounded-lg">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">
                  Maximum {maxImages} images reached
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Image Preview Grid */}
      {stagedImages.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700">
              Uploaded Images
            </h4>
            {unuploadedCount > 0 && (
              <Badge variant="outline" className="text-jamaica-green border-jamaica-green">
                {unuploadedCount} pending upload
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <AnimatePresence>
              {stagedImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  className={`relative group aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                    image.isPrimary 
                      ? 'border-jamaica-green shadow-lg shadow-jamaica-green/20' 
                      : image.isUploaded 
                        ? 'border-gray-200' 
                        : 'border-dashed border-gray-300'
                  }`}
                >
                  {/* Image */}
                  <img
                    src={image.url}
                    alt={`Product ${index + 1}`}
                    className={`w-full h-full object-cover transition-all duration-300 ${
                      image.isUploading ? 'blur-sm' : 'group-hover:scale-105'
                    }`}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/api/placeholder/200/200';
                    }}
                  />
                  
                  {/* Upload Progress Overlay */}
                  {image.isUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-center text-white">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                        <p className="text-xs">Uploading...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Error Overlay */}
                  {image.error && (
                    <div className="absolute inset-0 bg-red-500/90 flex items-center justify-center">
                      <div className="text-center text-white p-2">
                        <AlertCircle className="w-6 h-6 mx-auto mb-1" />
                        <p className="text-xs">{image.error}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Status Badges */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {image.isPrimary && (
                      <Badge className="bg-jamaica-green text-white text-xs px-1.5 py-0.5 h-auto">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        Primary
                      </Badge>
                    )}
                    {!image.isUploaded && !image.isUploading && !image.error && (
                      <Badge variant="outline" className="bg-white/90 text-xs px-1.5 py-0.5 h-auto">
                        Staged
                      </Badge>
                    )}
                  </div>
                  
                  {/* File Info */}
                  {image.file && (
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="bg-black/70 rounded px-2 py-1">
                        <p className="text-white text-xs truncate">{image.file.name}</p>
                        <p className="text-white/70 text-xs">{formatFileSize(image.file.size)}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex flex-col gap-1">
                      {!image.isPrimary && image.isUploaded && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setPrimary(image.id)}
                          className="h-8 w-8 p-0 bg-white/90 hover:bg-jamaica-green hover:text-white"
                          title="Set as primary image"
                        >
                          <Star className="w-3 h-3" />
                        </Button>
                      )}
                      
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => removeImage(image.id)}
                        className="h-8 w-8 p-0 bg-white/90 hover:bg-red-500 hover:text-white"
                        title="Remove image"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
} 